///
/// EarthBrowser
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type Earth } from "@/earthbrowser/activities/EarthActivity/Earth"
import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { CloudsOverlay, cRegionFrameMinutes } from "@/earthbrowser/dataset-distribution/clouds/CloudsOverlay"
import { CloudTextureFrame } from "@/earthbrowser/dataset-distribution/clouds/CloudTextureFrame"
import { CloudsTable, type CloudProperties } from "@/earthbrowser/dataset-distribution/clouds/CloudsTable"
import { CloudMosaicTable, type CloudMosaicFrameProperties } from "@/earthbrowser/dataset-distribution/clouds/CloudMosaicTable"
import { StormCloudsTable, type StormCloudProperties } from "@/earthbrowser/dataset-distribution/clouds/StormCloudsTable"
import { StormCloudTextureFrame } from "@/earthbrowser/dataset-distribution/clouds/StormCloudTextureFrame"
import { type LightningFlash, StormLightningNode } from "@/earthbrowser/dataset-distribution/clouds/StormLightningNode"
import { DatasetLayer, type ArrowRow, type DatasetSceneObject, type DatasetFeature, type DatasetManifest, type DatasetSyncConfig } from "@/earthbrowser/dataset-distribution/DatasetLayer"
import { engineBridge, isPaidEntitlement } from "@/earthbrowser/engineBridge"
import { isVideoRenderMode } from "@/earthbrowser/utils/renderMode"
import { time, timedelta } from "@stovokor/math/time"

// Frames are hourly GOES composites. Tier frame limits per the Phase 4 depth
// gates (SUBSCRIPTION_PRICING_MODEL.md): free 2 hours (one extra frame of
// ingest-lag margin), paid 48 hours. GPU memory stays bounded either way
// (the texture series purges frames outside the preload window).
const cPaidFrameLimit = 48
const cFreeFrameLimit = 3

function isPaidTier(): boolean {
	// No store (embed builds) means free-tier caps, unless render mode
	// raised the store-less default
	return isPaidEntitlement()
}

/** Effective frame limit for the current entitlement tier */
function frameLimit(): number {
	return isPaidTier() ? cPaidFrameLimit : cFreeFrameLimit
}

/** Storm cutouts (10-minute frames) use the same history depth, in hours */
function stormHistoryHours(): number {
	return isPaidTier() ? cPaidFrameLimit : cFreeFrameLimit
}

/**
 * CloudsDatasetLayer - Manages cloud overlay rendering
 *
 * This layer:
 * - Creates and manages the CloudOverlay for texture-based cloud rendering
 * - Loads cloud entries from the CloudsTable
 * - Creates TextureFrame objects for each cloud timestamp
 * - Handles visibility control
 * - Provides refresh capability when new data is synced
 */
export class CloudsDatasetLayer extends DatasetLayer<DatasetFeature> {
	private cloudOverlay: CloudsOverlay
	private earth: Earth
	// GLM flashes over storms (video render mode only; see StormLightningNode)
	private lightning?: StormLightningNode
	// Redux state subscription unsubscribe function
	private unsubscribe: (() => void) | null = null
	// Last seen entitlement tier, to re-query frames when depth changes
	// (e.g. checkout completes and the 48-hour window unlocks live)
	private lastPaidState = false

	constructor(context: EarthBrowserContext, earth: Earth) {
		super(context, "clouds")
		this.cloudOverlay = new CloudsOverlay(context)
		this.earth = earth

		// Initialize visibility based on current state
		const initialVisible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("cloud") ?? false
		this.visible = initialVisible
		this.lastPaidState = isPaidEntitlement()
		if (initialVisible) {
			this.earth.addOverlay(this.cloudOverlay)
		}

		// Subscribe to visibility and entitlement state changes
		this.unsubscribe = engineBridge.subscribe(() => {
			const state = engineBridge.getState()
			if (!state) return
			this.setVisible(state.earthbrowser.visibleDatasets.includes("cloud"))
			if (state.entitlements.isPaid !== this.lastPaidState) {
				this.lastPaidState = state.entitlements.isPaid
				void this.refresh()
			}
		})
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "clouds",
			pollIntervalMs: 20 * 60_000, // 20 minutes
			needsSync: true,
			supportsDelta: false,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await CloudsTable.create(db)
		await StormCloudsTable.create(db)
		await CloudMosaicTable.create(db)
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		// "all": the hourly global frames. "storms": storm-centered cutouts,
		// present only while a tropical cyclone is active; an absent shard
		// means no storms, so the table is emptied
		const shards = manifest.checkpoint.shards
		const global = shards.find(s => s.key === "all") ?? shards[0]
		if (!global) return

		const url = this.resolveUrl(global.url, global.path)
		console.log(`[CloudsDatasetLayer] Downloading shard: ${url}`)
		const rows = await downloadArrowRows(url)
		console.log(`[CloudsDatasetLayer] Parsed ${rows.length} rows`)

		const clouds = this.parseCloudRows(rows)
		await CloudsTable.replaceAll(clouds)

		const storms = shards.find(s => s.key === "storms")
		let stormRows: StormCloudProperties[] = []
		if (storms) {
			const stormUrl = this.resolveUrl(storms.url, storms.path)
			stormRows = this.parseStormCloudRows(await downloadArrowRows(stormUrl))
			console.log(`[CloudsDatasetLayer] Parsed ${stormRows.length} storm cutout rows`)
		}
		await StormCloudsTable.replaceAll(stormRows)

		// "lightning": GLM flashes inside storm boxes, held in memory and
		// drawn as sparks by the video render mode only (a strong hurricane
		// is ~100k rows a day; the app doesn't pay for them yet)
		const lightning = shards.find(s => s.key === "lightning")
		if (lightning && isVideoRenderMode()) {
			const rows = await downloadArrowRows(this.resolveUrl(lightning.url, lightning.path))
			this.setLightningFlashes(rows.map(r => ({
				stormId: String(r.storm_id),
				t: Number(r.t),
				lat: Number(r.lat),
				lon: Number(r.lon),
				energy: Number(r.energy),
			})))
			console.log(`[CloudsDatasetLayer] Loaded ${rows.length} lightning flashes`)
		}

		// "mosaic": the global ~2 km tile pyramids, one row per 10-minute frame
		const mosaic = shards.find(s => s.key === "mosaic")
		let mosaicRows: CloudMosaicFrameProperties[] = []
		if (mosaic) {
			const mosaicUrl = this.resolveUrl(mosaic.url, mosaic.path)
			mosaicRows = (await downloadArrowRows(mosaicUrl)).map(r => ({
				time: String(r.time),
				base_url: String(r.base_url),
				width: Number(r.width),
				height: Number(r.height),
				tile: Number(r.tile),
				levels: Number(r.levels),
			}))
			console.log(`[CloudsDatasetLayer] Parsed ${mosaicRows.length} mosaic frame rows`)
		}
		await CloudMosaicTable.replaceAll(mosaicRows)
	}

	/** Replace the flash set drawn over the storms (creates the node on first use) */
	public setLightningFlashes(flashes: LightningFlash[]): void {
		if (!this.lightning) {
			this.lightning = new StormLightningNode(this.context)
			this.addNode(this.lightning)
		}
		this.lightning.setFlashes(flashes)
	}

	/** Flashes currently loaded (render diagnostics) */
	public get lightningCount(): number {
		return this.lightning?.count ?? 0
	}

	/** Flash rate per minute in the display window ending at t; null without lightning data */
	public lightningRate(atMs: number): number | null {
		return this.lightning?.ratePerMinute(atMs) ?? null
	}

	private parseStormCloudRows(rows: ArrowRow[]): StormCloudProperties[] {
		return rows.map(r => ({
			storm_id: String(r.storm_id),
			time: String(r.time),
			url: String(r.url),
			satellite: String(r.satellite),
			min_lon: Number(r.min_lon),
			min_lat: Number(r.min_lat),
			max_lon: Number(r.max_lon),
			max_lat: Number(r.max_lat),
			width: Number(r.width),
			height: Number(r.height),
		}))
	}

	public async onSyncComplete(): Promise<void> {
		// Refresh the texture frames from the database
		await this.refresh()
	}

	private parseCloudRows(rows: ArrowRow[]): CloudProperties[] {
		return rows.map(r => ({
			time: String(r.time),
			url: String(r.url),
		}))
	}

	// ==================== Overlay Management ====================

	/**
	 * Refresh cloud data from the database and update frames
	 */
	public override async refresh(): Promise<void> {
		try {
			const clouds = await Database.db.exec(
				`select time, url from clouds order by time desc limit ?`,
				[frameLimit()],
				"low"
			) as CloudProperties[]

			console.log(`[CloudsDatasetLayer] Loaded ${clouds.length} cloud entries`)

			const frames = this.createFrames(clouds)
			this.cloudOverlay.setFrames(frames)

			const cutoff = new Date(Date.now() - stormHistoryHours() * 3600_000).toISOString()
			const stormClouds = await StormCloudsTable.getSince(cutoff)
			this.cloudOverlay.setStormFrames(stormClouds.map(row =>
				new StormCloudTextureFrame(this.context, row, new time(row.time), timedelta.minutes(cRegionFrameMinutes))))
			this.cloudOverlay.setMosaicFrames(await CloudMosaicTable.getSince(cutoff))
		} catch (error) {
			console.error("[CloudsDatasetLayer] Failed to refresh:", error)
		}
	}

	/**
	 * Create texture frames from cloud entries
	 */
	private createFrames(clouds: CloudProperties[]): CloudTextureFrame[] {
		const frames: CloudTextureFrame[] = []

		// Clouds are sorted desc by time, we want chronological order
		const sortedClouds = [...clouds].reverse()

		for (const cloud of sortedClouds) {
			const startTime = new time(cloud.time)
			const delta = timedelta.hours(1)
			const frame = new CloudTextureFrame(this.context, cloud.url, startTime, delta)
			frames.push(frame)
		}

		return frames
	}

	public override getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		const range = this.cloudOverlay.getTimeRange()
		return range ? { startMs: range.start.date.getTime(), endMs: range.end.date.getTime() } : null
	}

	/** The overlay, for the forecast sub-layer that draws through its region path */
	public get overlay(): CloudsOverlay {
		return this.cloudOverlay
	}

	public override getTimeLaneDetail(): TimeLaneDetail | null {
		return this.cloudOverlay.getTimeLaneDetail()
	}

	protected override frameLoadState(): LayerLoadState | null {
		return this.cloudOverlay.getLoadState()
	}

	/**
	 * Set visibility of the cloud layer
	 */
	public setVisible(visible: boolean): void {
		if (this.visible === visible) return

		this.visible = visible

		if (visible) {
			this.earth.addOverlay(this.cloudOverlay)
		} else {
			this.earth.removeOverlay(this.cloudOverlay)
			// Reclaim GPU memory (~11MB per loaded frame) while hidden;
			// frames reload from the SQLite image cache when re-enabled
			this.cloudOverlay.purge()
		}
	}

	// ==================== Scene Objects (not used for clouds) ====================

	public createSceneObject(_feature: DatasetFeature): DatasetSceneObject<DatasetFeature> | undefined {
		// Clouds don't use scene objects - they use texture overlays
		return undefined
	}

	override dispose(): void {
		// Unsubscribe from Redux state changes
		if (this.unsubscribe) {
			this.unsubscribe()
			this.unsubscribe = null
		}

		this.earth.removeOverlay(this.cloudOverlay)
		super.dispose()
	}
}
